import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { Account, AccountsService, IAccount } from '@services/accounts.service';
import { WebAppUser, AuthenticationService } from '@services/authentication.service';
import { Organisation, OrganisationDataService } from '@services/organisation-data.service';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, finalize } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';

@Component({
	selector: 'app-redirect',
	templateUrl: './redirect.component.html',
	styleUrls: ['./redirect.component.scss']
})
export class RedirectComponent implements OnDestroy {
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** currentUser */
	public currentUser: WebAppUser;

	/** accounts */
	public myAccounts: Account[] = [];

	/** organisations */
	public organisations: Organisation[] = [];

	/** targetURL */
	public targetURL: string = '';

	/** account */
	public account: IAccount;

	/** splitURL */
	public splitURL: string[] = [];

	/** gotSelectedAccount */
	public gotSelectedAccount: boolean = false;

	/**
	 * Default Constructor
	 * @param authenticationService authenticationService 
	 * @param siteSubscriptions siteSubscriptions 
	 * @param router router 
	 * @param route route 
	 * @param globalFunctions globalFunctions  
	 */
	constructor(
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		public organisationDataService: OrganisationDataService,
		public accountsService: AccountsService,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) {
		combineLatest([
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.myAccounts$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.myOrganisations$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, myAccounts, organisations, account]) => !!currentUser && !!myAccounts && !!organisations && !!account)
		).subscribe({
			next: ([currentUser, myAccounts, organisations, account]) => {
				if (!this.gotSelectedAccount) { //stopping this logic from running again when we update selected account
					this.currentUser = currentUser;
					this.account = account;
					this.account = this.siteSubscriptions.normalizeFeatures(this.account) as Account;
					this.gotSelectedAccount = true;
					this.myAccounts = myAccounts;
					this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
					this.organisations = organisations;
					this.targetURL = this.router.url;
					this.splitURL = this.router.url.split("/");

					/* Check whether the selected account has the organisation in the route */
					let orgMatch = this.organisations.find((x: Organisation) => x.Uid == this.splitURL[2]);

					if (orgMatch) {
						this.splitURL[1] = `${this.account.GUID}/organisation`;
						this.router.navigateByUrl(this.splitURL.join("/"));
					} else { /* find the correct account first */
						this.checkAccount();
					}
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * checkAccount
	 */
	async checkAccount() {
		let result = await this.getAccountGUID(this.splitURL[2]);
		let accountGUID: string = "";
		if (result[0].Result.includes("OK")) {
			accountGUID = result[0].Data
		}
		let accountMatch = this.myAccounts.find((x: IAccount) => x.GUID == accountGUID || "");
		if (accountMatch) {
			this.siteSubscriptions.SetSelectedAccount(accountMatch);
			this.siteSubscriptions.SetSelectedAccountGUID(accountMatch.GUID);
			this.siteSubscriptions.RefreshMyOrganisations(accountMatch.GUID);
			this.splitURL[1] = `${accountMatch.GUID}/organisation`;
			this.router.navigateByUrl(this.splitURL.join("/"));
		} else {
			this.router.navigate(['/unauthorised'], {
				state: {
					url: this.targetURL,
					message: "Account not found",
					source: "RedirectComponent"
				}
			});
		}
	}

	/**
	 * getAccountGUID
	 * @param organisationUID organisationUID 
	 * @returns 
	 */
	async getAccountGUID(organisationUID: string): Promise<any> {
		return new Promise<any>((resolve, reject) => {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.accountsService.getAccountGUID$(organisationUID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((accountGUID) => !!accountGUID),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.globalFunctions.displaySpinner(false);
					reject();
				})
			).subscribe({
				next: (accountGUID: any) => {
					resolve(accountGUID);
				},
				error: (error) => {
					if (error.status === 404) {

					}
					reject(error);
				}
			});
		})
	}
}
